import { AnnounceProvider } from '@fluentui/react-shared-contexts';
import { noop } from 'lodash';
import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { Announce } from './Announcer';
import Announcer from './Announcer';
import AnnouncerContext from './AnnouncerContext';

/**
 * Provides both the FUI9 AnnounceProvider context (for FUI9 components like MessageBar to make aria-live
 * announcements) and our custom AnnouncerContext. FUI9 announcements are routed through the existing
 * Yammer Announcer aria-live region, avoiding the need to import `tabster`, see:
 * https://github.com/microsoft/fluentui/issues/35896
 *
 * Note that modals have to have their own AnnouncerProvider. This is because aria-modal="true" tells screenreaders
 * to ignore everything outside of that region, including aria-live regions.
 */
const AnnouncerProvider: FC<PropsWithChildren> = ({ children }) => {
  const announceImplRef = useRef<Announce>(noop);
  const activeChildAnnouncerRef = useRef<Announce | null>(null);
  const parentAnnouncerProvider = useContext(AnnouncerContext);

  const setAnnounce = (value: Announce) => {
    announceImplRef.current = value;
  };

  const announce = useCallback(
    (text: string) => {
      const announceImpl = activeChildAnnouncerRef.current ?? announceImplRef.current;

      announceImpl(text);
    },
    [activeChildAnnouncerRef]
  );

  useEffect(() => {
    parentAnnouncerProvider.setActiveChildAnnouncer(announce);

    return () => {
      parentAnnouncerProvider.clearActiveChildAnnouncer();
    };
  }, [announce, parentAnnouncerProvider]);

  const setActiveChildAnnouncer = useCallback((value: Announce) => {
    activeChildAnnouncerRef.current = value;
  }, []);

  const clearActiveChildAnnouncer = useCallback(() => {
    activeChildAnnouncerRef.current = null;
  }, []);

  const contextValue = useMemo(
    () => ({
      announce,
      setActiveChildAnnouncer,
      clearActiveChildAnnouncer,
    }),
    [announce, setActiveChildAnnouncer, clearActiveChildAnnouncer]
  );

  const fui9ContextValue = useMemo(() => ({ announce }), [announce]);

  return (
    <>
      <AnnounceProvider value={fui9ContextValue}>
        <AnnouncerContext.Provider value={contextValue}>{children}</AnnouncerContext.Provider>
      </AnnounceProvider>
      <Announcer announceRef={setAnnounce} />
    </>
  );
};

export default AnnouncerProvider;
